// Ported from dev-scrips-pm-react src/documents/services/use-procedure-codes.ts (PROD-1119 documents module).
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';

/**
 * GET /api/Master/ProcedureCodesList?searchQuery={q}
 *
 * Master-data typeahead for CPT procedure codes. Used by the Procedure
 * document sub-form (PROD-797 PR 4) to populate `data.title` +
 * `data.code` on the patient document body.
 *
 * Flutter parity: `procedure_document_widget.dart` →
 * `FetchProcedureCodeUseCase` → `common_data_source_impl.dart`
 * `procedureCodeList()` (same endpoint).
 *
 * Note: an equivalent hook already exists in the Settings domain
 * (`src/settings/services/use-procedure-codes-search.ts`, typed for
 * Skills). We DON'T cross-import from settings — the type name there
 * (`StaffSpeciality`) is misleading in a documents context, and
 * cross-domain imports between `src/settings/` and `src/documents/`
 * would create a dependency cycle once the domain shells settle. The
 * two hooks are intentionally parallel and cheap to maintain.
 *
 * Response shape (verified against the existing settings hook +
 * `radiology_document_widget.dart` consumer):
 * ```json
 * [
 *   {
 *     "Id": "<guid>",
 *     "code": {
 *       "code": "00873",
 *       "displayName": "ANESTH KIDNEY STONE DESTRUCT",
 *       "system": "CPT",
 *       "description": "Anesthesia for lithotripsy, ..."
 *     }
 *   }
 * ]
 * ```
 *
 * Wire-shape gotcha: this endpoint's `code` object has `displayName`
 * (camelCase NAMED FIELD), not `display`. Consumers shipping a picked
 * row onto `PatientDocumentData` need to translate
 * `code.displayName → data.title` AND `code.code → data.code`.
 * `pickedCodeToData()` exported below encodes the translation.
 *
 * Empty / short queries short-circuit (CPT catalogue is thousands of
 * entries). 1-hour staleTime — catalogues change rarely.
 *
 * @see {@link ../../../docs/CONVERSION_NOTES_PROD-797.md} (PR 4 wire-shape gotcha table)
 */

export interface ProcedureCodeRow {
  /**
   * Row id. LIVE-PROBED 2026-06-10: the two master endpoints differ —
   * `ProcedureCodesList` ships `specialitySkillId`; `RadiologyCodesList`
   * ships `Id`. Both optional; consumers read `Id ?? specialitySkillId`.
   */
  Id?: string;
  specialitySkillId?: string;
  code: {
    code?: string;
    /**
     * Display string — ALSO differs by endpoint (live-probed 2026-06-10):
     * Procedure ships `displayName`, Radiology ships `display`. Read both
     * (`displayName ?? display`) or radiology rows render code-only.
     */
    displayName?: string;
    display?: string;
    system?: string;
    description?: string;
  };
}

export function useProcedureCodes(searchQuery: string) {
  const trimmed = searchQuery.trim();
  return useQuery<ProcedureCodeRow[]>({
    queryKey: ['procedure-codes', trimmed],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ searchQuery: trimmed });
      return get<ProcedureCodeRow[]>(
        `/api/Master/ProcedureCodesList?${qs.toString()}`,
        { signal },
      );
    },
    enabled: trimmed.length > 0,
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Translate a picked `ProcedureCodeRow` (or `RadiologyCodeRow` — same
 * shape) into the `PatientDocumentData` write shape (`{ title, code }`).
 *
 * The translation hides the `displayName → title` field rename so the
 * Procedure / Radiology sub-form callers don't repeat it. Returns
 * `null` if the row's `code.code` is missing (the consumer should
 * treat that as "not picked yet").
 */
export function pickedCodeToData(
  row: ProcedureCodeRow | null,
): { title: string; code: string } | null {
  if (!row?.code?.code) return null;
  return {
    // Procedure → displayName, Radiology → display (live-probed 2026-06-10).
    title: row.code.displayName ?? row.code.display ?? row.code.code,
    code: row.code.code,
  };
}
